export interface QuarterEnd {
  month: number;
  day: number;
}

export const QUARTER_ENDS: QuarterEnd[] = [
  { month: 3, day: 31 },  // Q1
  { month: 6, day: 30 },  // Q2
  { month: 9, day: 30 },  // Q3
  { month: 12, day: 31 }, // Q4
];

/**
 * Maps a quarter string to its index in QUARTER_ENDS.
 * 'Q1' → 0, 'Q2' → 1, 'Q3' → 2. Defaults to 2 for unrecognized input.
 */
export function getQuarterIndex(quarter: string): number {
  switch (quarter) {
    case 'Q1':
      return 0;
    case 'Q2':
      return 1;
    case 'Q3':
      return 2;
    default:
      return 2;
  }
}

/** A single quarterly column identity: 1-based quarter (1..4) and its full calendar year. */
export interface QuarterYear {
  quarter: number;
  year: number;
}

/**
 * Computes the ordered (quarter, year) pairs backing the five quarterly columns.
 *
 * Rolling: consecutive quarters going backwards, wrapping Q1 → Q4 of the prior year.
 * Comparable: the same quarter across consecutive years going backwards.
 *
 * This is the single source of the column-stepping logic: the date-string helpers below format
 * these pairs, and quarterly link building substitutes each column's quarter/year into the URL.
 *
 * Example (rolling): computeQuarterYearPairs(2026, 'Q3', false)
 *   → [{q:3,y:2026},{q:2,y:2026},{q:1,y:2026},{q:4,y:2025},{q:3,y:2025}]
 * Example (comparable): computeQuarterYearPairs(2026, 'Q3', true)
 *   → [{q:3,y:2026},{q:3,y:2025},{q:3,y:2024},{q:3,y:2023},{q:3,y:2022}]
 */
export function computeQuarterYearPairs(
  year: number | string,
  quarter: string,
  comparable: boolean,
  count: number = 5
): QuarterYear[] {
  const pairs: QuarterYear[] = [];
  let qIdx = getQuarterIndex(quarter);
  let currentYear = Number(year);

  for (let i = 0; i < count; i++) {
    pairs.push({ quarter: qIdx + 1, year: currentYear });

    if (comparable) {
      // Same quarter, step back one year.
      currentYear--;
    } else {
      // Rolling: step back one quarter, wrapping to Q4 of the prior year.
      qIdx--;
      if (qIdx < 0) {
        qIdx = 3;
        currentYear--;
      }
    }
  }

  return pairs;
}

/** Formats a (quarter, year) pair as its quarter-end date string, M/DD/YY. */
function formatQuarterEnd({ quarter, year }: QuarterYear): string {
  const { month, day } = QUARTER_ENDS[quarter - 1];
  const yy = String(year % 100).padStart(2, '0');
  return `${month}/${String(day).padStart(2, '0')}/${yy}`;
}

/**
 * Computes rolling quarter end dates going backwards through consecutive quarters.
 * Decrements year when crossing Q1→Q4 boundary.
 * Formats as M/DD/YY.
 *
 * Example: computeRollingQuarterDates(2026, 'Q3') → ['9/30/26', '6/30/26', '3/31/26', '12/31/25', '9/30/25']
 */
export function computeRollingQuarterDates(year: number, quarter: string, count: number = 5): string[] {
  return computeQuarterYearPairs(year, quarter, false, count).map(formatQuarterEnd);
}

/**
 * Computes comparable quarter end dates using the same quarter across consecutive years going backwards.
 * Formats as M/DD/YY.
 *
 * Example: computeComparableQuarterDates(2026, 'Q3') → ['9/30/26', '9/30/25', '9/30/24', '9/30/23', '9/30/22']
 */
export function computeComparableQuarterDates(year: number, quarter: string, count: number = 5): string[] {
  return computeQuarterYearPairs(year, quarter, true, count).map(formatQuarterEnd);
}
